import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, FileText, Search, Filter, Download, Globe, Lightbulb } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface WordData {
  word: string;
  count: number;
  density: number;
  category: 'stopword' | 'keyword' | 'regular';
}

interface TextAnalysis {
  totalWords: number;
  uniqueWords: number;
  avgWordsPerSentence: number;
  sentences: number;
  paragraphs: number;
  readingTime: number;
  wordFrequency: WordData[];
  keywordDensity: Array<{
    phrase: string;
    count: number;
    density: number;
    length: number;
  }>;
}

const WordDensityAnalyzer: React.FC = () => {
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<TextAnalysis | null>(null);
  const [minWordLength, setMinWordLength] = useState(3);
  const [excludeStopWords, setExcludeStopWords] = useState(true);
  const [phraseLength, setPhraseLength] = useState(2);
  const [inputMethod, setInputMethod] = useState<'text' | 'url'>('text');

  useSEO({
    title: 'Word Density Analyzer - SEO Keyword Density Checker',
    description: 'Analyze word frequency and keyword density for SEO optimization. Check text density, find keyword opportunities, optimize content.',
    keywords: 'word density analyzer, keyword density checker, SEO content analysis, text analysis, keyword frequency, content optimization',
    canonical: 'https://snap-tools.online/seo/word-density-analyzer'
  });

  // Common stop words
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'from', 'up', 'about', 'into', 'through', 'during', 'before', 'after', 'above', 'below',
    'between', 'among', 'under', 'around', 'near', 'beside', 'beyond', 'within', 'without',
    'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does',
    'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'shall',
    'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them',
    'my', 'your', 'his', 'her', 'its', 'our', 'their', 'mine', 'yours', 'ours', 'theirs',
    'this', 'that', 'these', 'those', 'here', 'there', 'where', 'when', 'why', 'how',
    'what', 'which', 'who', 'whom', 'whose', 'if', 'then', 'else', 'than', 'as', 'so'
  ]);

  // Analyze text
  const analyzeText = async (content: string) => {
    if (!content.trim()) {
      toast.error('Please enter text to analyze');
      return;
    }

    setIsAnalyzing(true);

    try {
      // Basic text statistics
      const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
      const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 0);
      const words = content.toLowerCase()
        .replace(/[^\w\s]/g, ' ')
        .split(/\s+/)
        .filter(word => word.length >= minWordLength);

      const totalWords = words.length;
      const uniqueWords = new Set(words).size;
      const avgWordsPerSentence = totalWords / sentences.length;
      const readingTime = Math.ceil(totalWords / 200); // Average reading speed: 200 WPM

      // Word frequency analysis
      const wordFreq = new Map<string, number>();
      words.forEach(word => {
        if (!excludeStopWords || !stopWords.has(word)) {
          wordFreq.set(word, (wordFreq.get(word) || 0) + 1);
        }
      });

      const wordFrequency: WordData[] = Array.from(wordFreq.entries())
        .map(([word, count]) => ({
          word,
          count,
          density: (count / totalWords) * 100,
          category: (stopWords.has(word) ? 'stopword' : 
                   count >= 3 ? 'keyword' : 'regular') as 'stopword' | 'keyword' | 'regular'
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 50);

      // Phrase analysis (n-grams)
      const phrases = new Map<string, number>();
      for (let i = 0; i <= words.length - phraseLength; i++) {
        const phrase = words.slice(i, i + phraseLength).join(' ');
        if (!excludeStopWords || !words.slice(i, i + phraseLength).every(w => stopWords.has(w))) {
          phrases.set(phrase, (phrases.get(phrase) || 0) + 1);
        }
      }

      const keywordDensity = Array.from(phrases.entries())
        .filter(([, count]) => count > 1)
        .map(([phrase, count]) => ({
          phrase,
          count,
          density: (count / (totalWords - phraseLength + 1)) * 100,
          length: phraseLength
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);

      const analysisResult: TextAnalysis = {
        totalWords,
        uniqueWords,
        avgWordsPerSentence,
        sentences: sentences.length,
        paragraphs: paragraphs.length,
        readingTime,
        wordFrequency,
        keywordDensity
      };

      setAnalysis(analysisResult);
      toast.success('Text analysis completed');
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error('Failed to analyze text');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Fetch content from URL
  const analyzeURL = async () => {
    if (!url) {
      toast.error('Please enter a URL to analyze');
      return;
    }

    setIsAnalyzing(true);

    try {
      // In a real implementation, you would fetch and extract text from the URL
      // For demo purposes, we'll simulate with mock content
      await new Promise(resolve => setTimeout(resolve, 2000));

      const mockContent = `
        Welcome to our comprehensive guide on digital marketing strategies for modern businesses. 
        Digital marketing has revolutionized how companies reach their target audience and drive growth.
        
        In today's competitive landscape, businesses must leverage multiple digital marketing channels 
        to maximize their online presence and achieve sustainable success. Content marketing, social media marketing, 
        and search engine optimization are fundamental pillars of any effective digital marketing strategy.
        
        Search engine optimization (SEO) plays a crucial role in digital marketing success. By optimizing your website 
        for search engines, you can increase organic visibility and attract qualified traffic to your business.
        
        Social media marketing enables businesses to engage directly with their audience and build meaningful relationships. 
        Platforms like Facebook, Instagram, and LinkedIn offer powerful advertising tools for targeted campaigns.
        
        Content marketing focuses on creating valuable, relevant content that attracts and retains customers. 
        High-quality content drives engagement, builds trust, and establishes thought leadership in your industry.
        
        Email marketing remains one of the most cost-effective digital marketing strategies with excellent ROI. 
        Personalized email campaigns can nurture leads and drive conversions effectively.
        
        Pay-per-click advertising provides immediate visibility and measurable results for your marketing campaigns. 
        Google Ads and social media advertising platforms offer sophisticated targeting options for maximum impact.
        
        Analytics and data-driven insights are essential for optimizing your digital marketing performance. 
        Regular analysis helps identify opportunities for improvement and ensures your strategies deliver results.
      `;

      setText(mockContent);
      await analyzeText(mockContent);
      toast.success('URL content fetched and analyzed');
    } catch (error) {
      console.error('URL analysis failed:', error);
      toast.error('Failed to analyze URL content');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Load example text
  const loadExample = () => {
    const exampleText = `
      Digital marketing encompasses all marketing efforts that use electronic devices and the internet. 
      Businesses leverage digital channels such as search engines, social media, email, and websites to connect with current and prospective customers.
      
      Search engine optimization (SEO) is a crucial component of digital marketing that focuses on improving website visibility in search engine results. 
      SEO involves optimizing content, technical elements, and building authority through quality backlinks.
      
      Content marketing is another essential digital marketing strategy that involves creating and distributing valuable, relevant content to attract and engage a target audience. 
      Quality content helps build trust, establish expertise, and drive profitable customer actions.
      
      Social media marketing utilizes platforms like Facebook, Instagram, Twitter, and LinkedIn to promote products or services. 
      These platforms offer powerful targeting options and enable direct customer engagement.
    `;
    setText(exampleText);
    toast.success('Example text loaded');
  };

  // Export analysis results
  const exportResults = () => {
    if (!analysis) return;

    const csvContent = [
      'Word Analysis Report',
      '',
      'Text Statistics:',
      `Total Words,${analysis.totalWords}`,
      `Unique Words,${analysis.uniqueWords}`,
      `Sentences,${analysis.sentences}`,
      `Paragraphs,${analysis.paragraphs}`,
      `Reading Time,${analysis.readingTime} minutes`,
      '',
      'Word Frequency:',
      'Word,Count,Density %',
      ...analysis.wordFrequency.map(w => `${w.word},${w.count},${w.density.toFixed(2)}`),
      '',
      'Keyword Phrases:',
      'Phrase,Count,Density %',
      ...analysis.keywordDensity.map(k => `"${k.phrase}",${k.count},${k.density.toFixed(2)}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'word-density-analysis.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Analysis exported successfully');
  };

  // Get density color
  const getDensityColor = (density: number) => {
    if (density >= 5) return 'text-red-600';
    if (density >= 3) return 'text-yellow-600';
    if (density >= 1) return 'text-green-600';
    return 'text-gray-600';
  };

  // Get density background color
  const getDensityBgColor = (density: number) => {
    if (density >= 5) return 'bg-red-100';
    if (density >= 3) return 'bg-yellow-100';
    if (density >= 1) return 'bg-green-100';
    return 'bg-gray-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <BarChart3 className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Word Density Analyzer</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Analyze word frequency and keyword density for SEO optimization. Identify keyword opportunities and optimize your content for better search rankings.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Method Selection */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Input</h3>
            
            <div className="flex bg-white rounded-lg p-1 mb-6">
              <button
                onClick={() => setInputMethod('text')}
                className={`flex-1 py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2 ${
                  inputMethod === 'text' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4" />
                Text Input
              </button>
              <button
                onClick={() => setInputMethod('url')}
                className={`flex-1 py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2 ${
                  inputMethod === 'url' 
                    ? 'bg-purple-600 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Globe className="w-4 h-4" />
                URL Analysis
              </button>
            </div>

            {inputMethod === 'text' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Text to Analyze
                  </label>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Paste your text content here for analysis..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={8}
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {text.split(/\s+/).filter(w => w.length > 0).length} words
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={loadExample}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Load Example
                  </button>
                  <button
                    onClick={() => analyzeText(text)}
                    disabled={isAnalyzing || !text.trim()}
                    className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    {isAnalyzing ? 'Analyzing...' : 'Analyze Text'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={analyzeURL}
                  disabled={isAnalyzing || !url}
                  className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Search className="w-4 h-4" />
                  {isAnalyzing ? 'Fetching & Analyzing...' : 'Analyze URL'}
                </button>
              </div>
            )}
          </div>

          {/* Analysis Settings */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Analysis Settings
            </h3>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Word Length
                </label>
                <select
                  value={minWordLength}
                  onChange={(e) => setMinWordLength(parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value={2}>2 characters</option>
                  <option value={3}>3 characters</option>
                  <option value={4}>4 characters</option>
                  <option value={5}>5 characters</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phrase Length
                </label>
                <select
                  value={phraseLength}
                  onChange={(e) => setPhraseLength(parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value={2}>2 words</option>
                  <option value={3}>3 words</option>
                  <option value={4}>4 words</option>
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludeStopWords}
                    onChange={(e) => setExcludeStopWords(e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Exclude Stop Words
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Analysis Results */}
          {analysis && (
            <div className="space-y-6 mb-8">
              {/* Text Statistics */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Text Statistics</h3>
                  <button
                    onClick={exportResults}
                    className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export Results
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">{analysis.totalWords}</div>
                    <div className="text-sm text-blue-700">Total Words</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{analysis.uniqueWords}</div>
                    <div className="text-sm text-green-700">Unique Words</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-600">{analysis.sentences}</div>
                    <div className="text-sm text-purple-700">Sentences</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">{analysis.paragraphs}</div>
                    <div className="text-sm text-yellow-700">Paragraphs</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-indigo-600">
                      {analysis.avgWordsPerSentence.toFixed(1)}
                    </div>
                    <div className="text-sm text-indigo-700">Avg Words/Sentence</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-600">{analysis.readingTime}m</div>
                    <div className="text-sm text-red-700">Reading Time</div>
                  </div>
                </div>
              </div>

              {/* Word Frequency */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <h4 className="font-semibold text-green-900 mb-4">Word Frequency</h4>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {analysis.wordFrequency.slice(0, 20).map((word, index) => (
                      <div key={word.word} className="bg-white rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                          <span className="font-medium">{word.word}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">{word.count}x</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDensityBgColor(word.density)} ${getDensityColor(word.density)}`}>
                            {word.density.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Keyword Phrases */}
                <div className="bg-purple-50 rounded-lg p-6">
                  <h4 className="font-semibold text-purple-900 mb-4">Keyword Phrases</h4>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {analysis.keywordDensity.map((phrase, index) => (
                      <div key={phrase.phrase} className="bg-white rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                          <span className="font-medium text-sm">{phrase.phrase}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">{phrase.count}x</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDensityBgColor(phrase.density)} ${getDensityColor(phrase.density)}`}>
                            {phrase.density.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    ))}
                    {analysis.keywordDensity.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No repeated phrases found</p>
                        <p className="text-sm">Try adjusting the phrase length or minimum word length</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Keyword Density Guidelines
              </h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Target Keywords:</strong> 1-3% density is generally optimal</li>
                <li>• <strong>Avoid Over-optimization:</strong> 5%+ density may trigger penalties</li>
                <li>• <strong>Natural Distribution:</strong> Spread keywords throughout content</li>
                <li>• <strong>LSI Keywords:</strong> Include related terms and synonyms</li>
                <li>• <strong>Long-tail Keywords:</strong> Target specific 3-4 word phrases</li>
                <li>• <strong>Content Quality:</strong> Focus on user value over keyword stuffing</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Content Optimization Tips</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Natural Flow:</strong> Write for users first, search engines second</li>
                <li>• <strong>Semantic Keywords:</strong> Use variations and related terms</li>
                <li>• <strong>Header Tags:</strong> Include keywords in H1, H2, H3 tags</li>
                <li>• <strong>Meta Elements:</strong> Optimize title tags and descriptions</li>
                <li>• <strong>Content Length:</strong> Longer content often ranks better</li>
                <li>• <strong>Readability:</strong> Keep sentences and paragraphs concise</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Analysis Interpretation</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>High Frequency Words:</strong> May indicate main topic focus</li>
                <li>• <strong>Repeated Phrases:</strong> Could be target keywords or over-optimization</li>
                <li>• <strong>Word Variety:</strong> Higher unique word count suggests richer content</li>
                <li>• <strong>Reading Time:</strong> Longer content may improve dwell time</li>
                <li>• <strong>Sentence Length:</strong> Shorter sentences improve readability</li>
                <li>• <strong>Paragraph Structure:</strong> Break up text for better UX</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">SEO Best Practices</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>Keyword Research:</strong> Use tools to find relevant keywords</li>
                <li>• <strong>Search Intent:</strong> Match content to user search intent</li>
                <li>• <strong>Content Clusters:</strong> Create topic-focused content groups</li>
                <li>• <strong>Internal Linking:</strong> Link related content with keyword anchors</li>
                <li>• <strong>Content Updates:</strong> Refresh content regularly</li>
                <li>• <strong>User Engagement:</strong> Focus on metrics like time on page</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WordDensityAnalyzer;
